import { defineComponent, h } from 'vue';
import { createdAt } from '@/ui/components/activityLogs/common/CreatedAt';
import { t } from '@/ui/i18n/i18n';
import { boldifyString, buildField } from '@/ui/components/activityLogs/helpers';
import { ActivityCreated } from '@/entities/activity/model';
import { IssueType } from '@/entities/issue';
import { user } from '@/ui/auth/auth';

interface CreatedProps {
  resourceName: string;
  log: ActivityCreated;
}

export const Created = defineComponent<CreatedProps>({
  name: 'Created',
  props: ['resourceName', 'log'],
  setup(props, context) {
    const onSeeDetailsClick = () => {
      context.emit('see-details:click', props.log);
    };

    const isInHoc = () => props.log.payload.type === IssueType.AdHoc;

    const isAutomaticIssue = () => props.log.payload.type === IssueType.Automatic;

    const isInForm = () => props.log.payload.type === IssueType.InForm;

    const isSensorAlarm = () => props.log.payload.type === IssueType.Sensor;

    const isManualInspection = () => props.log.payload.type === IssueType.ManualForm;

    const isAutomaticForm = () => props.log.payload.type === IssueType.AutomaticForm;

    const seeDetails = h('span', {
      class: 'activity-logs__see-details',
      attrs: { role: 'button' },
      on: { click: onSeeDetailsClick },
    },
    t('issues.details'));

    const seeReport = h('span', {
      class: 'activity-logs__see-details',
      attrs: { role: 'button' },
      on: { click: onSeeDetailsClick },
    },
    t('activity-logs.see-report'));

    return function render() {
      const content = () => {
        if (isManualInspection()) {
          return [
            h('span', {
              domProps: {
                innerHTML: t('activity-logs.manual-form-created', {
                  userName: boldifyString(props.log.user?.name ?? ''),
                }),
              },
            }),
            seeReport,
          ];
        }

        if (isInHoc()) {
          return [h('span', {
            domProps: {
              innerHTML: t('activity-logs.created', {
                user: boldifyString(props.log.user?.name ?? ''),
                resource: buildField(props.resourceName, user.value.locale),
              }),
            },
          })];
        }

        if (isAutomaticForm()) {
          return [h('span', {
            domProps: {
              innerHTML: t('activity-logs.automatic-form-created', {
                userName: boldifyString(props.log.user?.name ?? ''),
              }),
            },
          }), seeReport];
        }

        if (isAutomaticIssue()) {
          return [h('span', {
            domProps: {
              innerHTML: t('activity-logs.created-automatic-issue', {
                username: boldifyString(props.log.user?.name ?? ''),
                resource: props.resourceName,
              }),
            },
          }), seeDetails];
        }

        if (isInForm()) {
          return [h('span', {
            domProps: {
              innerHTML: t('activity-logs.created-issue-manual-from-inspection', {
                username: boldifyString(props.log.user?.name ?? ''),
              }),
            },
          }), seeDetails];
        }

        if (isSensorAlarm()) {
          return [h('span', {
            domProps: {
              innerHTML: t('issues.created-sensor', {
                sensorName: props.log.sensor?.data.title ?? '',
              }),
            },
          }), seeDetails];
        }

        return [h('span', {
          domProps: {
            innerHTML: t('activity-logs.created-automatic-issue', {
              username: boldifyString(props.log.user?.name ?? ''),
              resource: props.resourceName,
            }),
          },
        }), seeDetails];
      };

      return h('div', { class: 'activity-logs__log' }, [
        createdAt({ props: { log: props.log } }),
        content(),
      ]);
    };
  },
});
